import React, { useState } from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  VStack,
  HStack,
  SimpleGrid,
  useColorModeValue,
  Badge,
  Input,
  Textarea,
  Select,
  Checkbox,
  Radio,
  RadioGroup,
  Stack,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  Skeleton,
  SkeletonText,
  Progress,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Tooltip,
  Heading,
  Divider,
  Code,
} from "@chakra-ui/react";
import { toast } from "sonner";
import Card from "../../../components/Card/Card";
import CardBody from "../../../components/Card/CardBody";
import CardHeader from "../../../components/Card/CardHeader";

function UITesting() {
  const cardBg = useColorModeValue("white", "#151517");
  const borderColor = useColorModeValue("rgba(56, 187, 209, 0.2)", "rgba(56, 187, 209, 0.4)");
  const textColor = useColorModeValue("#575757", "white");
  const labelColor = useColorModeValue("gray.400", "gray.400");
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const [inputValue, setInputValue] = useState("");
  const [textareaValue, setTextareaValue] = useState("");
  const [selectValue, setSelectValue] = useState("");
  const [checkboxValue, setCheckboxValue] = useState(false);
  const [radioValue, setRadioValue] = useState("option1");

  const toastExamples = [
    {
      category: "Success",
      toasts: [
        {
          title: "Success Toast - Basic",
          description: "This is a basic success notification",
          status: "success" as const,
          duration: 3000,
          isClosable: true,
        },
        {
          title: "Trade logged successfully",
          description: "BTC/USDT trade has been logged.",
          status: "success" as const,
          duration: 3000,
          isClosable: true,
        },
        {
          title: "Connected",
          description: "Successfully connected to broker",
          status: "success" as const,
          duration: 3000,
          isClosable: true,
        },
        {
          title: "Profile updated",
          description: "Your profile information has been updated successfully.",
          status: "success" as const,
          duration: 3000,
          isClosable: true,
        },
      ],
    },
    {
      category: "Error",
      toasts: [
        {
          title: "Error Toast - Basic",
          description: "This is a basic error notification",
          status: "error" as const,
          duration: 5000,
          isClosable: true,
        },
        {
          title: "Connection failed",
          description: "Failed to connect to broker. Please check your credentials.",
          status: "error" as const,
          duration: 5000,
          isClosable: true,
        },
        {
          title: "Error logging trade",
          description: "An error occurred while logging the trade. Please try again.",
          status: "error" as const,
          duration: 5000,
          isClosable: true,
        },
        {
          title: "Error updating profile",
          description: "Failed to update profile. Please try again.",
          status: "error" as const,
          duration: 5000,
          isClosable: true,
        },
      ],
    },
    {
      category: "Warning",
      toasts: [
        {
          title: "Warning Toast - Basic",
          description: "This is a basic warning notification",
          status: "warning" as const,
          duration: 4000,
          isClosable: true,
        },
        {
          title: "Access Denied",
          description: "You don't have access to this feature. Please contact support if you believe this is an error.",
          status: "warning" as const,
          duration: 5000,
          isClosable: true,
        },
        {
          title: "Trade Expiring Soon",
          description: "Your trade setup will expire in 15 seconds.",
          status: "warning" as const,
          duration: 4000,
          isClosable: true,
        },
      ],
    },
    {
      category: "Info",
      toasts: [
        {
          title: "Info Toast - Basic",
          description: "This is a basic info notification",
          status: "info" as const,
          duration: 3000,
          isClosable: true,
        },
        {
          title: "Trade Synced",
          description: "Your trades have been synced with your broker account.",
          status: "info" as const,
          duration: 3000,
          isClosable: true,
        },
        {
          title: "New Setup Available",
          description: "A new trade setup is now available for XAU/USD.",
          status: "info" as const,
          duration: 3000,
          isClosable: true,
        },
      ],
    },
  ];

  const SectionCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <Card bg={cardBg} borderRadius="lg" boxShadow="sm" mb={6}>
      <CardHeader pb={4}>
        <Heading size="md" color={textColor} fontFamily="'Urbanist', sans-serif">
          {title}
        </Heading>
      </CardHeader>
      <CardBody>
        {children}
      </CardBody>
    </Card>
  );

  return (
    <Flex direction="column" pt={6} pb={8}>
      {/* Page Title */}
      <Text
        fontSize={{ base: "2xl", md: "3xl" }}
        fontWeight="normal"
        color="#38BBD1"
        mb={6}
        fontFamily="'Urbanist', sans-serif"
      >
        UI Component Testing
      </Text>

      {/* Buttons Section */}
      <SectionCard title="Buttons">
        <VStack spacing={6} align="stretch">
          <Box>
            <Text fontSize="xs" color={labelColor} mb={3} fontFamily="'IBM Plex Sans', sans-serif" textTransform="uppercase">
              Primary Gradient Buttons
            </Text>
            <HStack spacing={3} flexWrap="wrap">
              <Button
                bgGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
                color="white"
                display="inline-flex"
                alignItems="center"
                gap="0.5rem"
                transition="all 0.3s ease"
                px="20px"
                py="10px"
                _hover={{
                  pr: "calc(20px + 0.5rem)",
                }}
                sx={{
                  "& .button-arrow": {
                    width: "1.25rem",
                    height: "1.25rem",
                    transition: "transform 0.3s ease",
                    color: "white",
                  },
                  "&:hover .button-arrow": {
                    transform: "translateX(6px)",
                  },
                }}
              >
                Primary Button
                <Box
                  as="svg"
                  className="button-arrow"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  w="1.25rem"
                  h="1.25rem"
                  color="white"
                >
                  <path
                    fillRule="evenodd"
                    d="M12.97 3.97a.75.75 0 0 1 1.06 0l7.5 7.5a.75.75 0 0 1 0 1.06l-7.5 7.5a.75.75 0 1 1-1.06-1.06l6.22-6.22H3a.75.75 0 0 1 0-1.5h16.19l-6.22-6.22a.75.75 0 0 1 0-1.06Z"
                    clipRule="evenodd"
                  />
                </Box>
              </Button>
              <Button
                bgGradient="linear-gradient(135deg, #06E363, #AAFAB1)"
                color="white"
                display="inline-flex"
                alignItems="center"
                gap="0.5rem"
                transition="all 0.3s ease"
                px="20px"
                py="10px"
                _hover={{
                  pr: "calc(20px + 0.5rem)",
                }}
                sx={{
                  "& .button-arrow": {
                    width: "1.25rem",
                    height: "1.25rem",
                    transition: "transform 0.3s ease",
                    color: "white",
                  },
                  "&:hover .button-arrow": {
                    transform: "translateX(6px)",
                  },
                }}
              >
                Success Button
                <Box
                  as="svg"
                  className="button-arrow"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  w="1.25rem"
                  h="1.25rem"
                  color="white"
                >
                  <path
                    fillRule="evenodd"
                    d="M12.97 3.97a.75.75 0 0 1 1.06 0l7.5 7.5a.75.75 0 0 1 0 1.06l-7.5 7.5a.75.75 0 1 1-1.06-1.06l6.22-6.22H3a.75.75 0 0 1 0-1.5h16.19l-6.22-6.22a.75.75 0 0 1 0-1.06Z"
                    clipRule="evenodd"
                  />
                </Box>
              </Button>
              <Button
                bgGradient="linear-gradient(135deg, #E00101, #FE4244)"
                color="white"
                display="inline-flex"
                alignItems="center"
                gap="0.5rem"
                transition="all 0.3s ease"
                px="20px"
                py="10px"
                _hover={{
                  pr: "calc(20px + 0.5rem)",
                }}
                sx={{
                  "& .button-arrow": {
                    width: "1.25rem",
                    height: "1.25rem",
                    transition: "transform 0.3s ease",
                    color: "white",
                  },
                  "&:hover .button-arrow": {
                    transform: "translateX(6px)",
                  },
                }}
              >
                Error Button
                <Box
                  as="svg"
                  className="button-arrow"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  w="1.25rem"
                  h="1.25rem"
                  color="white"
                >
                  <path
                    fillRule="evenodd"
                    d="M12.97 3.97a.75.75 0 0 1 1.06 0l7.5 7.5a.75.75 0 0 1 0 1.06l-7.5 7.5a.75.75 0 1 1-1.06-1.06l6.22-6.22H3a.75.75 0 0 1 0-1.5h16.19l-6.22-6.22a.75.75 0 0 1 0-1.06Z"
                    clipRule="evenodd"
                  />
                </Box>
              </Button>
              <Button
                bgGradient="linear-gradient(135deg, #F45B0B, #F49113)"
                color="white"
                display="inline-flex"
                alignItems="center"
                gap="0.5rem"
                transition="all 0.3s ease"
                px="20px"
                py="10px"
                _hover={{
                  pr: "calc(20px + 0.5rem)",
                }}
                sx={{
                  "& .button-arrow": {
                    width: "1.25rem",
                    height: "1.25rem",
                    transition: "transform 0.3s ease",
                    color: "white",
                  },
                  "&:hover .button-arrow": {
                    transform: "translateX(6px)",
                  },
                }}
              >
                Warning Button
                <Box
                  as="svg"
                  className="button-arrow"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  w="1.25rem"
                  h="1.25rem"
                  color="white"
                >
                  <path
                    fillRule="evenodd"
                    d="M12.97 3.97a.75.75 0 0 1 1.06 0l7.5 7.5a.75.75 0 0 1 0 1.06l-7.5 7.5a.75.75 0 1 1-1.06-1.06l6.22-6.22H3a.75.75 0 0 1 0-1.5h16.19l-6.22-6.22a.75.75 0 0 1 0-1.06Z"
                    clipRule="evenodd"
                  />
                </Box>
              </Button>
            </HStack>
          </Box>

          <Box>
            <Text fontSize="xs" color={labelColor} mb={3} fontFamily="'IBM Plex Sans', sans-serif" textTransform="uppercase">
              Outline Buttons
            </Text>
            <HStack spacing={3} flexWrap="wrap">
              <Button variant="outline" borderColor="gray.400" color="gray.400" fontSize="xs" fontWeight="medium" textTransform="uppercase" fontFamily="'IBM Plex Sans', sans-serif">
                Outline Button
              </Button>
              <Button variant="outline" borderColor="#38BBD1" color="#38BBD1">
                Primary Outline
              </Button>
              <Button variant="outline" borderColor="#06E363" color="#06E363">
                Success Outline
              </Button>
            </HStack>
          </Box>

          <Box>
            <Text fontSize="xs" color={labelColor} mb={3} fontFamily="'IBM Plex Sans', sans-serif" textTransform="uppercase">
              Button Sizes
            </Text>
            <HStack spacing={3} flexWrap="wrap">
              <Button 
                size="sm" 
                bgGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)" 
                color="white"
                display="inline-flex"
                alignItems="center"
                gap="0.5rem"
                transition="all 0.3s ease"
                _hover={{
                  pr: "calc(12px + 0.5rem)",
                }}
                sx={{
                  "& .button-arrow": {
                    width: "1.25rem",
                    height: "1.25rem",
                    transition: "transform 0.3s ease",
                    color: "white",
                  },
                  "&:hover .button-arrow": {
                    transform: "translateX(6px)",
                  },
                }}
              >
                Small
                <Box
                  as="svg"
                  className="button-arrow"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  w="1.25rem"
                  h="1.25rem"
                  color="white"
                >
                  <path
                    fillRule="evenodd"
                    d="M12.97 3.97a.75.75 0 0 1 1.06 0l7.5 7.5a.75.75 0 0 1 0 1.06l-7.5 7.5a.75.75 0 1 1-1.06-1.06l6.22-6.22H3a.75.75 0 0 1 0-1.5h16.19l-6.22-6.22a.75.75 0 0 1 0-1.06Z"
                    clipRule="evenodd"
                  />
                </Box>
              </Button>
              <Button 
                size="md" 
                bgGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)" 
                color="white"
                display="inline-flex"
                alignItems="center"
                gap="0.5rem"
                transition="all 0.3s ease"
                _hover={{
                  pr: "calc(16px + 0.5rem)",
                }}
                sx={{
                  "& .button-arrow": {
                    width: "1.25rem",
                    height: "1.25rem",
                    transition: "transform 0.3s ease",
                    color: "white",
                  },
                  "&:hover .button-arrow": {
                    transform: "translateX(6px)",
                  },
                }}
              >
                Medium
                <Box
                  as="svg"
                  className="button-arrow"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  w="1.25rem"
                  h="1.25rem"
                  color="white"
                >
                  <path
                    fillRule="evenodd"
                    d="M12.97 3.97a.75.75 0 0 1 1.06 0l7.5 7.5a.75.75 0 0 1 0 1.06l-7.5 7.5a.75.75 0 1 1-1.06-1.06l6.22-6.22H3a.75.75 0 0 1 0-1.5h16.19l-6.22-6.22a.75.75 0 0 1 0-1.06Z"
                    clipRule="evenodd"
                  />
                </Box>
              </Button>
              <Button 
                size="lg" 
                bgGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)" 
                color="white"
                display="inline-flex"
                alignItems="center"
                gap="0.5rem"
                transition="all 0.3s ease"
                _hover={{
                  pr: "calc(24px + 0.5rem)",
                }}
                sx={{
                  "& .button-arrow": {
                    width: "1.25rem",
                    height: "1.25rem",
                    transition: "transform 0.3s ease",
                    color: "white",
                  },
                  "&:hover .button-arrow": {
                    transform: "translateX(6px)",
                  },
                }}
              >
                Large
                <Box
                  as="svg"
                  className="button-arrow"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  w="1.25rem"
                  h="1.25rem"
                  color="white"
                >
                  <path
                    fillRule="evenodd"
                    d="M12.97 3.97a.75.75 0 0 1 1.06 0l7.5 7.5a.75.75 0 0 1 0 1.06l-7.5 7.5a.75.75 0 1 1-1.06-1.06l6.22-6.22H3a.75.75 0 0 1 0-1.5h16.19l-6.22-6.22a.75.75 0 0 1 0-1.06Z"
                    clipRule="evenodd"
                  />
                </Box>
              </Button>
            </HStack>
          </Box>

          <Box>
            <Text fontSize="xs" color={labelColor} mb={3} fontFamily="'IBM Plex Sans', sans-serif" textTransform="uppercase">
              Button States
            </Text>
            <HStack spacing={3} flexWrap="wrap">
              <Button 
                bgGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)" 
                color="white"
                display="inline-flex"
                alignItems="center"
                gap="0.5rem"
                transition="all 0.3s ease"
                px="20px"
                py="10px"
                _hover={{
                  pr: "calc(20px + 0.5rem)",
                }}
                sx={{
                  "& .button-arrow": {
                    width: "1.25rem",
                    height: "1.25rem",
                    transition: "transform 0.3s ease",
                    color: "white",
                  },
                  "&:hover .button-arrow": {
                    transform: "translateX(6px)",
                  },
                }}
              >
                Normal
                <Box
                  as="svg"
                  className="button-arrow"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  w="1.25rem"
                  h="1.25rem"
                  color="white"
                >
                  <path
                    fillRule="evenodd"
                    d="M12.97 3.97a.75.75 0 0 1 1.06 0l7.5 7.5a.75.75 0 0 1 0 1.06l-7.5 7.5a.75.75 0 1 1-1.06-1.06l6.22-6.22H3a.75.75 0 0 1 0-1.5h16.19l-6.22-6.22a.75.75 0 0 1 0-1.06Z"
                    clipRule="evenodd"
                  />
                </Box>
              </Button>
              <Button bgGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)" color="white" isLoading>Loading</Button>
              <Button bgGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)" color="white" isDisabled>Disabled</Button>
            </HStack>
          </Box>
        </VStack>
      </SectionCard>

      {/* Badges Section */}
      <SectionCard title="Badges">
        <VStack spacing={4} align="stretch">
          <Box>
            <Text fontSize="xs" color={labelColor} mb={3} fontFamily="'IBM Plex Sans', sans-serif" textTransform="uppercase">
              Gradient Badges
            </Text>
            <HStack spacing={3} flexWrap="wrap">
              <Badge bgGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)" color="white" px={3} py={1.5} borderRadius="md" fontSize="xs" fontWeight="bold">
                Primary
              </Badge>
              <Badge bgGradient="linear-gradient(135deg, #06E363, #AAFAB1)" color="white" px={3} py={1.5} borderRadius="md" fontSize="xs" fontWeight="bold">
                Success
              </Badge>
              <Badge bgGradient="linear-gradient(135deg, #E00101, #FE4244)" color="white" px={3} py={1.5} borderRadius="md" fontSize="xs" fontWeight="bold">
                Error
              </Badge>
              <Badge bgGradient="linear-gradient(135deg, #F45B0B, #F49113)" color="white" px={3} py={1.5} borderRadius="md" fontSize="xs" fontWeight="bold">
                Warning
              </Badge>
            </HStack>
          </Box>

          <Box>
            <Text fontSize="xs" color={labelColor} mb={3} fontFamily="'IBM Plex Sans', sans-serif" textTransform="uppercase">
              Trade Type Badges
            </Text>
            <HStack spacing={3} flexWrap="wrap">
              <Badge bgGradient="linear-gradient(135deg, #06E363, #AAFAB1)" color="white" fontSize="xs" fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium" textTransform="uppercase" p="3px 10px" borderRadius="8px">
                LONG
              </Badge>
              <Badge bgGradient="linear-gradient(135deg, #E00101, #FE4244)" color="white" fontSize="xs" fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium" textTransform="uppercase" p="3px 10px" borderRadius="8px">
                SHORT
              </Badge>
            </HStack>
          </Box>
        </VStack>
      </SectionCard>

      {/* Form Elements Section */}
      <SectionCard title="Form Elements">
        <VStack spacing={6} align="stretch">
          <FormControl>
            <FormLabel fontFamily="'IBM Plex Sans', sans-serif" fontSize="xs" color={labelColor} fontWeight="medium" textTransform="uppercase">
              Input Field
            </FormLabel>
            <Input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Enter text here..."
              fontFamily="'IBM Plex Sans', sans-serif"
              borderColor={borderColor}
            />
          </FormControl>

          <FormControl>
            <FormLabel fontFamily="'IBM Plex Sans', sans-serif" fontSize="xs" color={labelColor} fontWeight="medium" textTransform="uppercase">
              Textarea
            </FormLabel>
            <Textarea
              value={textareaValue}
              onChange={(e) => setTextareaValue(e.target.value)}
              placeholder="Enter multiple lines of text..."
              rows={4}
              fontFamily="'IBM Plex Sans', sans-serif"
              borderColor={borderColor}
            />
          </FormControl>

          <FormControl>
            <FormLabel fontFamily="'IBM Plex Sans', sans-serif" fontSize="xs" color={labelColor} fontWeight="medium" textTransform="uppercase">
              Select Dropdown
            </FormLabel>
            <Select
              value={selectValue}
              onChange={(e) => setSelectValue(e.target.value)}
              placeholder="Select an option..."
              fontFamily="'IBM Plex Sans', sans-serif"
              borderColor={borderColor}
            >
              <option value="option1">Option 1</option>
              <option value="option2">Option 2</option>
              <option value="option3">Option 3</option>
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel fontFamily="'IBM Plex Sans', sans-serif" fontSize="xs" color={labelColor} fontWeight="medium" textTransform="uppercase">
              Checkbox
            </FormLabel>
            <Checkbox
              isChecked={checkboxValue}
              onChange={(e) => setCheckboxValue(e.target.checked)}
              fontFamily="'IBM Plex Sans', sans-serif"
            >
              Check this option
            </Checkbox>
          </FormControl>

          <FormControl>
            <FormLabel fontFamily="'IBM Plex Sans', sans-serif" fontSize="xs" color={labelColor} fontWeight="medium" textTransform="uppercase">
              Radio Buttons
            </FormLabel>
            <RadioGroup value={radioValue} onChange={setRadioValue}>
              <Stack direction="row" spacing={4}>
                <Radio value="option1" fontFamily="'IBM Plex Sans', sans-serif">Option 1</Radio>
                <Radio value="option2" fontFamily="'IBM Plex Sans', sans-serif">Option 2</Radio>
                <Radio value="option3" fontFamily="'IBM Plex Sans', sans-serif">Option 3</Radio>
              </Stack>
            </RadioGroup>
          </FormControl>
        </VStack>
      </SectionCard>

      {/* Typography Section */}
      <SectionCard title="Typography">
        <VStack spacing={6} align="stretch">
          <Box>
            <Text fontSize="xs" color={labelColor} mb={3} fontFamily="'IBM Plex Sans', sans-serif" textTransform="uppercase">
              Headings (Urbanist)
            </Text>
            <VStack align="flex-start" spacing={2}>
              <Heading as="h1" size="2xl" fontFamily="'Urbanist', sans-serif" color={textColor}>Heading 1</Heading>
              <Heading as="h2" size="xl" fontFamily="'Urbanist', sans-serif" color={textColor}>Heading 2</Heading>
              <Heading as="h3" size="lg" fontFamily="'Urbanist', sans-serif" color={textColor}>Heading 3</Heading>
              <Heading as="h4" size="md" fontFamily="'Urbanist', sans-serif" color={textColor}>Heading 4</Heading>
              <Heading as="h5" size="sm" fontFamily="'Urbanist', sans-serif" color={textColor}>Heading 5</Heading>
              <Heading as="h6" size="xs" fontFamily="'Urbanist', sans-serif" color={textColor}>Heading 6</Heading>
            </VStack>
          </Box>

          <Box>
            <Text fontSize="xs" color={labelColor} mb={3} fontFamily="'IBM Plex Sans', sans-serif" textTransform="uppercase">
              Body Text (Urbanist)
            </Text>
            <VStack align="flex-start" spacing={2}>
              <Text fontSize="lg" fontFamily="'Urbanist', sans-serif" color={textColor}>Large body text (18px)</Text>
              <Text fontSize="md" fontFamily="'Urbanist', sans-serif" color={textColor}>Base body text (16px)</Text>
              <Text fontSize="sm" fontFamily="'Urbanist', sans-serif" color={textColor}>Small body text (14px)</Text>
            </VStack>
          </Box>

          <Box>
            <Text fontSize="xs" color={labelColor} mb={3} fontFamily="'IBM Plex Sans', sans-serif" textTransform="uppercase">
              Labels & Captions (IBM Plex Sans)
            </Text>
            <VStack align="flex-start" spacing={2}>
              <Text fontSize="sm" fontFamily="'IBM Plex Sans', sans-serif" color={labelColor} textTransform="uppercase" letterSpacing="0.05em">
                Label Text (Uppercase)
              </Text>
              <Text fontSize="xs" fontFamily="'IBM Plex Sans', sans-serif" color={labelColor} textTransform="uppercase" letterSpacing="0.05em">
                Caption Text (Uppercase)
              </Text>
            </VStack>
          </Box>

          <Box>
            <Text fontSize="xs" color={labelColor} mb={3} fontFamily="'IBM Plex Sans', sans-serif" textTransform="uppercase">
              Font Weights
            </Text>
            <VStack align="flex-start" spacing={2}>
              <Text fontWeight="normal" fontFamily="'Urbanist', sans-serif" color={textColor}>Normal (400)</Text>
              <Text fontWeight="medium" fontFamily="'Urbanist', sans-serif" color={textColor}>Medium (500)</Text>
              <Text fontWeight="semibold" fontFamily="'Urbanist', sans-serif" color={textColor}>Semibold (600)</Text>
              <Text fontWeight="bold" fontFamily="'Urbanist', sans-serif" color={textColor}>Bold (700)</Text>
            </VStack>
          </Box>
        </VStack>
      </SectionCard>

      {/* Color Palette Section */}
      <SectionCard title="Color Palette">
        <VStack spacing={6} align="stretch">
          <Box>
            <Text fontSize="xs" color={labelColor} mb={3} fontFamily="'IBM Plex Sans', sans-serif" textTransform="uppercase">
              Primary Brand Colors
            </Text>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
              <Box>
                <Box w="100%" h="80px" bg="#38BBD1" borderRadius="md" mb={2} />
                <Text fontSize="xs" fontFamily="'IBM Plex Sans', sans-serif" color={textColor}>Tropical Surf</Text>
                <Code fontSize="xs">#38BBD1</Code>
              </Box>
              <Box>
                <Box w="100%" h="80px" bg="#73E6F0" borderRadius="md" mb={2} />
                <Text fontSize="xs" fontFamily="'IBM Plex Sans', sans-serif" color={textColor}>Sky Glacier</Text>
                <Code fontSize="xs">#73E6F0</Code>
              </Box>
              <Box>
                <Box w="100%" h="80px" bg="#3EEAEC" borderRadius="md" mb={2} />
                <Text fontSize="xs" fontFamily="'IBM Plex Sans', sans-serif" color={textColor}>Neon Aqua</Text>
                <Code fontSize="xs">#3EEAEC</Code>
              </Box>
              <Box>
                <Box w="100%" h="80px" bg="#96FBF7" borderRadius="md" mb={2} />
                <Text fontSize="xs" fontFamily="'IBM Plex Sans', sans-serif" color={textColor}>Mint Breeze</Text>
                <Code fontSize="xs">#96FBF7</Code>
              </Box>
            </SimpleGrid>
          </Box>

          <Box>
            <Text fontSize="xs" color={labelColor} mb={3} fontFamily="'IBM Plex Sans', sans-serif" textTransform="uppercase">
              Gradient Combinations
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Box>
                <Box w="100%" h="60px" bgGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)" borderRadius="md" mb={2} />
                <Text fontSize="xs" fontFamily="'IBM Plex Sans', sans-serif" color={textColor}>Primary Gradient</Text>
              </Box>
              <Box>
                <Box w="100%" h="60px" bgGradient="linear-gradient(135deg, #06E363, #AAFAB1)" borderRadius="md" mb={2} />
                <Text fontSize="xs" fontFamily="'IBM Plex Sans', sans-serif" color={textColor}>Glacial Green</Text>
              </Box>
              <Box>
                <Box w="100%" h="60px" bgGradient="linear-gradient(135deg, #E00101, #FE4244)" borderRadius="md" mb={2} />
                <Text fontSize="xs" fontFamily="'IBM Plex Sans', sans-serif" color={textColor}>Glacial Red</Text>
              </Box>
              <Box>
                <Box w="100%" h="60px" bgGradient="linear-gradient(135deg, #F45B0B, #F49113)" borderRadius="md" mb={2} />
                <Text fontSize="xs" fontFamily="'IBM Plex Sans', sans-serif" color={textColor}>Glacial Orange</Text>
              </Box>
            </SimpleGrid>
          </Box>
        </VStack>
      </SectionCard>

      {/* Alerts Section */}
      <SectionCard title="Alerts">
        <VStack spacing={4} align="stretch">
          <Alert status="success" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>Success!</AlertTitle>
              <AlertDescription>Your action was completed successfully.</AlertDescription>
            </Box>
          </Alert>
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>Error!</AlertTitle>
              <AlertDescription>An error occurred. Please try again.</AlertDescription>
            </Box>
          </Alert>
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>Warning!</AlertTitle>
              <AlertDescription>Please review this information carefully.</AlertDescription>
            </Box>
          </Alert>
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>Information</AlertTitle>
              <AlertDescription>Here's some helpful information for you.</AlertDescription>
            </Box>
          </Alert>
        </VStack>
      </SectionCard>

      {/* Loading States Section */}
      <SectionCard title="Loading States">
        <VStack spacing={6} align="stretch">
          <Box>
            <Text fontSize="xs" color={labelColor} mb={3} fontFamily="'IBM Plex Sans', sans-serif" textTransform="uppercase">
              Spinners
            </Text>
            <HStack spacing={4}>
              <VStack>
                <Spinner size="sm" color="#38BBD1" />
                <Text fontSize="xs" color={textColor}>Small</Text>
              </VStack>
              <VStack>
                <Spinner size="md" color="#38BBD1" />
                <Text fontSize="xs" color={textColor}>Medium</Text>
              </VStack>
              <VStack>
                <Spinner size="lg" color="#38BBD1" />
                <Text fontSize="xs" color={textColor}>Large</Text>
              </VStack>
              <VStack>
                <Spinner size="xl" color="#38BBD1" />
                <Text fontSize="xs" color={textColor}>Extra Large</Text>
              </VStack>
            </HStack>
          </Box>

          <Box>
            <Text fontSize="xs" color={labelColor} mb={3} fontFamily="'IBM Plex Sans', sans-serif" textTransform="uppercase">
              Skeletons
            </Text>
            <VStack spacing={3} align="stretch">
              <Skeleton height="20px" />
              <Skeleton height="20px" />
              <SkeletonText mt="4" noOfLines={3} spacing="4" />
            </VStack>
          </Box>

          <Box>
            <Text fontSize="xs" color={labelColor} mb={3} fontFamily="'IBM Plex Sans', sans-serif" textTransform="uppercase">
              Progress Bars
            </Text>
            <VStack spacing={3} align="stretch">
              <Box>
                <Text fontSize="xs" color={textColor} mb={1}>25%</Text>
                <Progress value={25} colorScheme="cyan" borderRadius="full" />
              </Box>
              <Box>
                <Text fontSize="xs" color={textColor} mb={1}>50%</Text>
                <Progress value={50} colorScheme="cyan" borderRadius="full" />
              </Box>
              <Box>
                <Text fontSize="xs" color={textColor} mb={1}>75%</Text>
                <Progress value={75} colorScheme="cyan" borderRadius="full" />
              </Box>
              <Box>
                <Text fontSize="xs" color={textColor} mb={1}>100%</Text>
                <Progress value={100} colorScheme="cyan" borderRadius="full" />
              </Box>
            </VStack>
          </Box>
        </VStack>
      </SectionCard>

      {/* Modals Section */}
      <SectionCard title="Modals">
        <VStack spacing={4} align="stretch">
          <Button 
            onClick={onModalOpen} 
            bgGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)" 
            color="white"
            display="inline-flex"
            alignItems="center"
            gap="0.5rem"
            transition="all 0.3s ease"
            px="20px"
            py="10px"
            _hover={{
              pr: "calc(20px + 0.5rem)",
            }}
            sx={{
              "& .button-arrow": {
                width: "1.25rem",
                height: "1.25rem",
                transition: "transform 0.3s ease",
                color: "white",
              },
              "&:hover .button-arrow": {
                transform: "translateX(6px)",
              },
            }}
          >
            Open Modal Example
            <Box
              as="svg"
              className="button-arrow"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              w="1.25rem"
              h="1.25rem"
              color="white"
            >
              <path
                fillRule="evenodd"
                d="M12.97 3.97a.75.75 0 0 1 1.06 0l7.5 7.5a.75.75 0 0 1 0 1.06l-7.5 7.5a.75.75 0 1 1-1.06-1.06l6.22-6.22H3a.75.75 0 0 1 0-1.5h16.19l-6.22-6.22a.75.75 0 0 1 0-1.06Z"
                clipRule="evenodd"
              />
            </Box>
          </Button>
          <Text fontSize="sm" color={labelColor} fontFamily="'IBM Plex Sans', sans-serif">
            Click the button above to see a modal example
          </Text>
        </VStack>

        <Modal isOpen={isModalOpen} onClose={onModalClose} isCentered>
          <ModalOverlay backdropFilter="blur(10px)" bg="rgba(0, 0, 0, 0.1)" />
          <ModalContent bg={cardBg} borderRadius="16px">
            <ModalHeader color="#38BBD1" fontFamily="'Urbanist', sans-serif" fontWeight="normal">
              Modal Example
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <Text fontFamily="'Urbanist', sans-serif" color={textColor}>
                This is an example modal. You can use this pattern for dialogs, confirmations, and forms.
              </Text>
            </ModalBody>
          </ModalContent>
        </Modal>
      </SectionCard>

      {/* Tooltips Section */}
      <SectionCard title="Tooltips">
        <HStack spacing={4} flexWrap="wrap">
          <Tooltip label="This is a tooltip" placement="top" hasArrow>
            <Button>Hover me (Top)</Button>
          </Tooltip>
          <Tooltip label="This is a tooltip" placement="bottom" hasArrow>
            <Button>Hover me (Bottom)</Button>
          </Tooltip>
          <Tooltip label="This is a tooltip" placement="left" hasArrow>
            <Button>Hover me (Left)</Button>
          </Tooltip>
          <Tooltip label="This is a tooltip" placement="right" hasArrow>
            <Button>Hover me (Right)</Button>
          </Tooltip>
        </HStack>
      </SectionCard>

      {/* Toast Notifications Section */}
      <Card bg={cardBg} borderRadius="lg" boxShadow="sm" mb={6} p={0}>
        <CardHeader pb={4} px={6} pt={6}>
          <Heading size="md" color={textColor} fontFamily="'Urbanist', sans-serif">
            Toast Notifications
          </Heading>
        </CardHeader>
        <CardBody pt={0} px={6} pb={6} w="100%" display="flex" flexDirection="column" alignItems="flex-start">
          <Text fontSize="sm" color={labelColor} mb={6} fontFamily="'IBM Plex Sans', sans-serif" textTransform="uppercase" letterSpacing="0.05em">
            Click any button to preview the toast notification
          </Text>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} w="100%" minW="0" mx={0} px={0}>
          {toastExamples.map((category) => (
            <Card
              key={category.category}
              bg={cardBg}
              borderRadius="16px"
              border="1px solid"
              borderColor={borderColor}
              boxShadow="sm"
              w="100%"
              minW="0"
              p={0}
              mx={0}
            >
              <CardHeader pb={4} px={4} pt={4}>
                <Text
                  fontSize="lg"
                  fontWeight="bold"
                  color={textColor}
                  fontFamily="'IBM Plex Sans', sans-serif"
                >
                  {category.category}
                </Text>
              </CardHeader>
              <CardBody pt={0} px={4} pb={4}>
                <VStack align="stretch" spacing={3}>
                  {category.toasts.map((toastConfig, index) => (
                    <Box
                      key={index}
                      p={4}
                      borderRadius="md"
                      border="1px solid"
                      borderColor={borderColor}
                      bg={useColorModeValue("gray.50", "gray.800")}
                    >
                      <VStack align="stretch" spacing={2} mb={3}>
                        <Text
                          fontSize="xs"
                          color={labelColor}
                          fontFamily="'IBM Plex Sans', sans-serif"
                          textTransform="uppercase"
                          letterSpacing="0.05em"
                        >
                          Title
                        </Text>
                        <Text
                          fontSize="sm"
                          color={textColor}
                          fontFamily="'Urbanist', sans-serif"
                          fontWeight="medium"
                        >
                          {toastConfig.title}
                        </Text>
                      </VStack>
                      <VStack align="stretch" spacing={2} mb={4}>
                        <Text
                          fontSize="xs"
                          color={labelColor}
                          fontFamily="'IBM Plex Sans', sans-serif"
                          textTransform="uppercase"
                          letterSpacing="0.05em"
                        >
                          Description
                        </Text>
                        <Text
                          fontSize="sm"
                          color={textColor}
                          fontFamily="'Urbanist', sans-serif"
                        >
                          {toastConfig.description}
                        </Text>
                      </VStack>
                      <HStack spacing={2} fontSize="xs" color={labelColor} mb={3}>
                        <Text fontFamily="'IBM Plex Sans', sans-serif">
                          Duration: {toastConfig.duration}ms
                        </Text>
                        <Text>•</Text>
                        <Text fontFamily="'IBM Plex Sans', sans-serif">
                          Closable: {toastConfig.isClosable ? "Yes" : "No"}
                        </Text>
                      </HStack>
                      <Button
                        size="sm"
                        variant="outline"
                        borderColor="gray.400"
                        color="gray.400"
                        fontSize="xs"
                        fontWeight="medium"
                        textTransform="uppercase"
                        fontFamily="'IBM Plex Sans', sans-serif"
                        onClick={() => {
                          const method = toastConfig.status === "success" ? toast.success
                            : toastConfig.status === "error" ? toast.error
                            : toastConfig.status === "warning" ? toast.warning
                            : toast.info;
                          method(toastConfig.title, { description: toastConfig.description });
                        }}
                        w="100%"
                      >
                        Show Toast
                      </Button>
                    </Box>
                  ))}
                </VStack>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>
        </CardBody>
      </Card>
    </Flex>
  );
}

export default UITesting;
